import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Activity, List, Rocket, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LegacyLink } from "@/components/layout/LegacyLink";
import { cn } from "@/utils/cn";
import { canModifyDeployments } from "../permissions";

export type DeploymentsSection = "list" | "quick" | "new" | "switch" | "observability";

const itemClass = (active: boolean) =>
  cn(
    "inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    active
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:bg-muted hover:text-foreground",
  );

/**
 * Sub-navigation of the Deployments section. The list is still a legacy screen,
 * so it is reached through LegacyLink; the Omnimizer subsections are routes.
 */
export function DeploymentsSubnav({
  current,
  className,
}: {
  current: DeploymentsSection;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const nav = useRef<HTMLElement>(null);
  // On narrow screens the ribbon scrolls: bring the current tab into view.
  useEffect(() => {
    // Scroll only the ribbon: scrollIntoView would also shift the page's own containers.
    const ribbon = nav.current;
    if (!ribbon) return;
    const center = () => {
      const active = ribbon.querySelector<HTMLElement>('[aria-current="page"]');
      if (!active || ribbon.scrollWidth <= ribbon.clientWidth) return;
      const a = active.getBoundingClientRect();
      const r = ribbon.getBoundingClientRect();
      ribbon.scrollLeft += a.left + a.width / 2 - (r.left + r.width / 2);
    };
    center();
    // Keep following mobile resizes and translated labels for the whole mount.
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(center);
    observer?.observe(ribbon);
    const active = ribbon.querySelector<HTMLElement>('[aria-current="page"]');
    if (active) observer?.observe(active);
    window.addEventListener("resize", center);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", center);
    };
  }, [current, i18n.language]);
  const items: { key: DeploymentsSection; label: string; icon: LucideIcon }[] = [
    { key: "list", label: t("omnimizer.nav.deployments"), icon: List },
    { key: "quick", label: t("omnimizer.nav.quick"), icon: Zap },
    { key: "new", label: t("omnimizer.nav.deploy"), icon: Rocket },
    { key: "switch", label: t("omnimizer.nav.switch"), icon: Zap },
    { key: "observability", label: t("omnimizer.nav.observability"), icon: Activity },
  ];
  const content = (label: string, Icon: LucideIcon) => (
    <>
      <Icon className="size-4" aria-hidden />
      {label}
    </>
  );
  return (
    <nav
      ref={nav}
      aria-label={t("omnimizer.nav.label")}
      className={cn(
        "flex w-full max-w-full min-w-0 gap-1 overflow-x-auto rounded-full border border-border bg-card p-1 sm:w-fit",
        className,
      )}
    >
      {items.filter(({ key }) => key !== "new" || canModifyDeployments()).map(({ key, label, icon }) => {
        const active = current === key;
        const common = {
          className: itemClass(active),
          "aria-current": active ? ("page" as const) : undefined,
        };
        if (key === "list" || key === "quick")
          return (
            <LegacyLink exact key={key} to={key === "quick" ? "/predefined-models" : "/deployments"} {...common}>
              {content(label, icon)}
            </LegacyLink>
          );
        const to =
          key === "new"
              ? "/deployments/new"
              : key === "switch" ? "/deployments/fast-switch" : "/deployments/observability";
        return (
          <Link key={key} to={to} activeOptions={{ exact: true }} {...common}>
            {content(label, icon)}
          </Link>
        );
      })}
    </nav>
  );
}
